import { Brain, Check, Clock, Mic, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import AssistantFace from "@/components/AssistantFace";
import { DISH, EX, Initials, Pill } from "./primitives";

/* Shared white card that floats inside the grey visual box */
function Panel({ className, children }) {
  return (
    <div className={cn("absolute inset-[18px] flex flex-col rounded-xl border border-line bg-white px-[18px] py-4 sm:inset-[22px]", className)}>
      {children}
    </div>
  );
}

function Row({ ini, tone, name, sub, right }) {
  return (
    <div className="flex items-center gap-2.5 border-t border-line py-2">
      <Initials text={ini} tone={tone} size={30} />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-[13px] font-medium">{name}</span>
        <span className="truncate text-xs text-neutral-500">{sub}</span>
      </div>
      {right}
    </div>
  );
}

/* ---------- Coaching ---------- */

export function TrainingVisual() {
  const tiles = [EX.benchM, EX.gobletF, EX.ohpM, EX.hipF];
  return (
    <Panel className="gap-3 overflow-hidden py-3.5">
      <div className="flex shrink-0 items-center gap-1.5 overflow-hidden">
        {["Chest", "Legs", "Shoulders", "Glutes", "Back"].map((f, i) => (
          <span key={f} className={cn("shrink-0 rounded-full px-2.5 py-1 text-[11.5px] font-medium", i === 0 ? "bg-ink text-white" : "bg-soft text-neutral-600")}>
            {f}
          </span>
        ))}
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-2 grid-rows-2 gap-2">
        {tiles.map((t) => (
          <div key={t.src} className="relative flex min-h-0 flex-col overflow-hidden rounded-lg border border-line bg-white">
            <img src={t.src} alt={`${t.name} demo`} loading="lazy" className="min-h-0 w-full flex-1 scale-125 object-cover" />
            <span className="absolute left-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-md bg-ink/85">
              <Play className="h-2.5 w-2.5 fill-lemon text-lemon" />
            </span>
            <span className="relative shrink-0 truncate bg-white px-2 py-1.5 text-[11.5px] font-medium">{t.name}</span>
          </div>
        ))}
      </div>
      <div className="flex shrink-0 items-center justify-between text-[11.5px] text-neutral-500">
        <span>5,900+ exercises</span>
        <span className="flex overflow-hidden rounded-full border border-line">
          <span className="bg-ink px-2 py-0.5 text-white">Men</span>
          <span className="px-2 py-0.5">Women</span>
        </span>
      </div>
    </Panel>
  );
}

function Macro({ label, value, pct, color }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex justify-between text-[12.5px]">
        <span className="text-neutral-600">{label}</span>
        <b className="font-semibold">{value}</b>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-[#EEEEEE]">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

export function NutritionVisual() {
  return (
    <Panel className="gap-3.5 py-[18px]">
      <div className="flex items-baseline justify-between">
        <span className="text-[13px] text-neutral-500">Today</span>
        <span className="text-xs text-neutral-500">Target 1,900 kcal</span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-[34px] font-semibold tracking-[-0.8px]">1,420</span>
        <span className="text-[13px] text-neutral-500">kcal eaten</span>
      </div>
      <Macro label="Protein" value="112 / 140 g" pct={80} color="#171717" />
      <Macro label="Carbs" value="150 / 190 g" pct={79} color="#A3A3A3" />
      <Macro label="Fat" value="44 / 60 g" pct={73} color="#D4D4D4" />
      <div className="mt-1 flex flex-col gap-1.5">
        {[DISH.omelette].map((d) => (
          <div key={d.src} className="flex items-center gap-2.5 rounded-lg bg-soft p-1.5 pr-2.5 text-[12.5px]">
            <img src={d.src} alt={d.name} loading="lazy" className="h-9 w-9 rounded-md object-cover" />
            <span className="min-w-0 flex-1 truncate">{d.name}</span>
            <span className="text-neutral-500">{d.kcal}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

export function CheckinsVisual() {
  return (
    <Panel>
      <div className="flex items-center justify-between pb-2.5">
        <span className="text-[13.5px] font-semibold">Weekly check-ins</span>
        <Pill tone="lemon">5 to review</Pill>
      </div>
      <Row ini="TW" tone="blue" name="Tom Walker" sub="Weight 82.4 kg · sleep 7.5 h" right={<Pill tone="dark">New</Pill>} />
      <Row ini="JM" tone="violet" name="James Moore" sub="Energy 8/10 · 4 photos" right={<Pill tone="dark">New</Pill>} />
      <Row ini="DO" tone="orange" name="Daniel Obi" sub="Waist down 2 cm" right={<Pill>Reviewed</Pill>} />
      <Row ini="SC" tone="amber" name="Sarah Chen" sub="First check-in due Monday" right={<Pill>Upcoming</Pill>} />
      <Row ini="AR" tone="pink" name="Aisha Rahman" sub="Missed last week" right={<Pill tone="amber">Overdue</Pill>} />
    </Panel>
  );
}

/* ---------- AI Agent ---------- */

export function ProposalVisual() {
  return (
    <Panel className="overflow-hidden p-0 sm:p-0">
      <div className="flex flex-col gap-1 border-b border-line px-4 py-3 text-[12.5px]">
        <div>
          <span className="text-neutral-500">To </span>Sarah Chen
        </div>
        <div>
          <span className="text-neutral-500">Subject </span>
          <b className="font-semibold">Your 12 week plan with Martin</b>
        </div>
      </div>
      <p className="m-0 flex-1 px-4 py-3.5 text-[13px] leading-[21px] text-neutral-600">
        Hi Sarah, great to meet you today. Here is the plan we talked about: three strength
        sessions a week, a 1,900 kcal nutrition plan and a check-in every Monday. Your first
        session is Thursday at 7am.
      </p>
      <div className="flex items-center gap-2 border-t border-line px-4 py-3">
        <span className="flex min-w-0 items-center gap-1.5 truncate text-xs text-neutral-500">
          <AssistantFace className="h-5 w-5" />
          Drafted for you
        </span>
        <span className="ml-auto flex h-[30px] shrink-0 items-center whitespace-nowrap rounded-lg border border-neutral-200 px-3 text-[12.5px] font-medium">
          Edit
        </span>
        <span className="flex h-[30px] shrink-0 items-center whitespace-nowrap rounded-lg bg-black px-3 text-[12.5px] font-medium text-white">
          Approve &amp; send
        </span>
      </div>
    </Panel>
  );
}

function Reminder({ title, when, done }) {
  return (
    <div className="flex items-center gap-2.5 border-t border-line py-[11px]">
      {done ? (
        <span className="flex h-[18px] w-[18px] items-center justify-center rounded-md bg-ink">
          <Check className="h-[11px] w-[11px] text-lemon" strokeWidth={3} />
        </span>
      ) : (
        <span className="h-[18px] w-[18px] rounded-md border-[1.5px] border-neutral-300" />
      )}
      <span className={cn("flex-1 text-[13px]", done && "text-neutral-400 line-through")}>{title}</span>
      <span className="flex items-center gap-1 text-xs text-neutral-500">
        <Clock className="h-3 w-3" strokeWidth={2} />
        {when}
      </span>
    </div>
  );
}

export function RemindersVisual() {
  return (
    <Panel>
      <div className="flex items-center gap-2 pb-3">
        <span className="text-[13.5px] font-semibold">Reminders</span>
        <span className="ml-auto text-xs text-neutral-500">Thursday</span>
      </div>
      <Reminder title="Follow up Sarah if no reply" when="Sat 9:00" />
      <Reminder title="Ask Aisha how the week went" when="Today 18:00" />
      <Reminder title="Renewal call with Daniel" when="Fri 12:30" />
      <Reminder title="Send Tom his deload week" when="Mon 8:00" />
      <Reminder title="Thank James for the review" when="Done" done />
      <div className="mt-auto flex items-center gap-2 rounded-[10px] bg-ink px-3 py-2.5">
        <Mic className="h-3.5 w-3.5 text-lemon" strokeWidth={2.2} />
        <span className="text-[12.5px] text-white">"Remind me to call Daniel Friday"</span>
      </div>
    </Panel>
  );
}

export function InboxVisual() {
  return (
    <Panel>
      <div className="flex items-center gap-2 pb-2.5">
        <span className="text-[13.5px] font-semibold">Email tracking</span>
        <Pill tone="lemon">2 waiting</Pill>
      </div>
      <Row ini="SC" tone="amber" name="Sarah Chen" sub="Your 12 week plan with Martin" right={<Pill>Opened twice</Pill>} />
      <Row ini="AR" tone="pink" name="Aisha Rahman" sub="Quick check on your week" right={<Pill tone="amber">No reply · 3 d</Pill>} />
      <Row ini="DO" tone="orange" name="Daniel Obi" sub="Renewing for another block" right={<Pill tone="green">Replied</Pill>} />
      <Row ini="LP" tone="cyan" name="Lena Park" sub="See you back in November" right={<Pill>Sent</Pill>} />
      <Row ini="TW" tone="blue" name="Tom Walker" sub="Deload week plan" right={<Pill>Scheduled</Pill>} />
    </Panel>
  );
}

/* ---------- What the assistant makes ---------- */

function SaysBubble({ children }) {
  return (
    <div className="flex items-start gap-2">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-lemon/30">
        <AssistantFace className="h-7 w-7" />
      </span>
      <p className="m-0 rounded-2xl rounded-tl-md bg-soft px-3 py-2 text-[12.5px] leading-[18px]">{children}</p>
    </div>
  );
}

export function MealPlanVisual() {
  return (
    <Panel className="gap-3 py-3.5">
      <SaysBubble>Arman's plan is ready: 2,200 kcal, no dairy. I made a photo of every dish.</SaysBubble>
      <div className="grid min-h-0 flex-1 grid-cols-2 grid-rows-2 gap-2">
        {Object.values(DISH).map((d) => (
          <div key={d.src} className="relative min-h-0 overflow-hidden rounded-lg">
            <img src={d.src} alt={d.name} loading="lazy" className="h-full w-full object-cover" />
            <span className="absolute bottom-1.5 left-1.5 rounded-md bg-white/95 px-1.5 py-0.5 text-[11px] font-medium">{d.kcal} kcal</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

export function VideosVisual() {
  return (
    <Panel className="gap-3 py-3.5">
      <div className="flex items-center gap-2.5 pb-1">
        <Initials text="DO" tone="orange" size={30} />
        <div className="flex flex-col">
          <span className="text-[13px] font-medium">Daniel Obi</span>
          <span className="text-xs text-neutral-500">Chat</span>
        </div>
      </div>
      <div className="self-end rounded-2xl rounded-br-md bg-ink px-3 py-2 text-[12.5px] text-white">Your chest videos for this week 👇</div>
      <div className="flex flex-col gap-2 self-end">
        {[EX.benchM, EX.dbBenchF].map((e) => (
          <div key={e.src} className="flex w-[220px] items-center gap-2.5 rounded-xl border border-line bg-white p-1.5 pr-2.5">
            <div className="relative h-[52px] w-[64px] shrink-0 overflow-hidden rounded-md border border-line">
              <img src={e.src} alt={`${e.name} demo`} loading="lazy" className="h-full w-full scale-[1.6] object-cover" />
              <span className="absolute bottom-1 left-1 flex h-4 w-4 items-center justify-center rounded-full bg-ink/85">
                <Play className="h-2 w-2 fill-lemon text-lemon" />
              </span>
            </div>
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-[12px] font-medium">{e.name}</span>
              <span className="text-[11px] text-neutral-500">{e.part} · video</span>
            </div>
          </div>
        ))}
      </div>
      <span className="mt-auto self-end text-[11px] text-neutral-400">Sent after you tapped approve</span>
    </Panel>
  );
}

export function MemoryVisual() {
  const facts = [
    ["Sarah", "Hates burpees. Swap for step-ups."],
    ["Tom", "Trains at 6am before work."],
    ["Aisha", "Vegetarian, loves spicy food."],
    ["You", "Check-ins go out on Mondays."],
  ];
  return (
    <Panel className="gap-2.5">
      <div className="flex items-center gap-2 pb-1">
        <Brain className="h-4 w-4" strokeWidth={1.9} />
        <span className="text-[13.5px] font-semibold">What it remembers</span>
      </div>
      {facts.map(([who, what]) => (
        <div key={who} className="flex items-start gap-2 border-t border-line pt-2.5 text-[12.5px]">
          <b className="w-12 shrink-0 font-semibold">{who}</b>
          <span className="text-neutral-600">{what}</span>
        </div>
      ))}
      <div className="mt-auto flex items-center gap-2 rounded-[10px] bg-ink px-3 py-2.5">
        <Mic className="h-3.5 w-3.5 text-lemon" strokeWidth={2.2} />
        <span className="text-[12.5px] text-white">"Remember Aisha is vegetarian"</span>
      </div>
    </Panel>
  );
}
